import React, { useEffect, useState, useRef } from "react";
import Button from "./Button";
import InputField from "./InputField";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  saveAsInputColumn,
  getEditStateData,
  getInputColumnsData,
  setunSavedRulesChanges,
} from "../store/workspaceSlice";
import DropDownMenu from "./DropDownMenu";
import { columnDataTypesOptions } from "../utils";
import DefineCellData from "../popups/DefineCellData";
import CheckBox from "./CheckBox";

const initialInputColumnData = {
  inColChr: "",
  inColDT: "",
  replaceColumnData: false,
  inputDateFormat: "",
  toDateFormat: "",
  inColDesc: "",
  inColCharCode: "",
  inColContactName: "",
  inColDueDate: "",
  replacingData: [],
  inColAccountNo: "",
  inColDcCode: "",
  inColExchangeRate: "",
  makeColumnAbsolute: false,
  makeItInteger: false,
  negateColumnData: false,
};

export const dateFormats = [
  { label: "yyyy-MM-dd", value: "yyyy-MM-dd" },
  { label: "MM/dd/yyyy", value: "MM/dd/yyyy" },
  { label: "MM-dd-yyyy", value: "MM-dd-yyyy" },
  { label: "dd-MM-yyyy", value: "dd-MM-yyyy" },
  { label: "dd/MM/yyyy", value: "dd/MM/yyyy" },
  { label: "dd/MM/yy", value: "dd/MM/yy" },
  { label: "MM/dd/yy", value: "MM/dd/yy" },
  { label: "d-MMM-yy", value: "d-MMM-yy" },
  { label: "dd-MMM-yy", value: "dd-MMM-yy" },
  { label: "MMMM dd, yyyy", value: "MMMM dd, yyyy" },
  { label: "Full Date & Time", value: "PPpp" },
];

const dueDateOptions = [
  { value: "custom", label: "Enter Custom due date" },
  { value: "SameDayDue", label: "Same-Day Due" }, // Additional option
  { value: "EndOfTheMonth", label: "End of the Month" }, // Additional option
  { value: "dueIn1month", label: "Due in 1 Month" }, // Additional option
  ...Array.from({ length: 30 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`, // Convert to string for display
  })),
];

const InputColumnForm = () => {
  const [mode, setMode] = useState(null);
  const state = useSelector(getEditStateData);
  const existingInputColumns = useSelector(getInputColumnsData);
  const dispatch = useDispatch();
  let [isOpen, setIsOpen] = useState(false);
  const [inputColumnData, setInputColumnData] = useState(
    initialInputColumnData,
  );
  const [inputCharCopy, setinputCharCopy] = useState("");
  const [copyFromColumn, setCopyFromColumn] = useState("");
  const [isInLocalEditMode, setIsInLocalEditMode] = useState(false);
  const inputColumnRef = useRef(null);
  const [customDueDate, setCustomDueDate] = useState("");
  const resetTrigger = useSelector(
    (state) => state.workspace.resetInputColumnForm,
  );

  useEffect(() => {
    focusFirstField();
  }, []);

  useEffect(() => {
    reset();
  }, [resetTrigger]);

  useEffect(() => {
    if (state?.type === "inputColumnForm") {
      setInputColumnData(state.data);
      setIsInLocalEditMode(true);
      checkDueDate();
      setMode("new");
    }
  }, [state]);

  const focusFirstField = () => {
    setTimeout(() => {
      inputColumnRef.current?.focus();
    }, 0);
  };

  const checkDueDate = () => {
    const savedDueDate = state.data?.inColDueDate;

    const predefinedValues = [
      "SameDayDue",
      "EndOfTheMonth",
      "dueIn1month",
      ...Array.from({ length: 30 }, (_, i) => i + 1),
    ];

    const normalizedValue = isNaN(savedDueDate)
      ? savedDueDate
      : Number(savedDueDate);

    if (
      normalizedValue !== undefined &&
      normalizedValue !== "" &&
      !predefinedValues.includes(normalizedValue)
    ) {
      setCustomDueDate(savedDueDate);
      setInputColumnData((prev) => ({ ...prev, inColDueDate: "custom" }));
    }
  };
  const handleOnChange = (e) => {
    setInputColumnData((prevData) => ({
      ...prevData,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

    dispatch(setunSavedRulesChanges(true));
  };

  const isSaveDisabled =
    !inputColumnData.inColChr ||
    !inputColumnData.inColDT ||
    (inputColumnData.inColDT === "3" && !inputColumnData.inputDateFormat);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isSaveDisabled) {
      e.preventDefault();
      handleInsertColumn();
    }
  };

  const handleInsertColumn = () => {
    const newColChar = inputColumnData.inColChr?.trim();

    // Check if it already exists
    const alreadyExists = existingInputColumns.find(
      (col) => col.inColChr === newColChar,
    );

    toast.dismiss();
    if (alreadyExists && !isInLocalEditMode) {
      toast.error(`Column '${newColChar}' already exists!`);
      return;
    }

    toast.dismiss();
    dispatch(
      saveAsInputColumn({
        ...inputColumnData,
        inColDueDate:
          inputColumnData.inColDueDate === "custom"
            ? customDueDate
            : inputColumnData.inColDueDate,
      }),
    );

    toast.success(
      `Column '${inputColumnData.inColChr}' ${
        isInLocalEditMode ? "updated" : "added"
      } successfully`,
    );
    reset();
  };

  const reset = () => {
    setInputColumnData(initialInputColumnData);
    setIsInLocalEditMode(false);
    focusFirstField();
    setMode(null);
  };

  const handleCopyCase = () => {
    const newColChar = inputCharCopy?.trim();

    // Check if it already exists
    const alreadyExists = existingInputColumns.find(
      (col) => col.inColChr === newColChar,
    );

    toast.dismiss();
    if (alreadyExists) {
      toast.error(`Column '${newColChar}' already exists!`);
      return;
    }

    const columnToCopy = existingInputColumns.find(
      (col) => col.inColChr === copyFromColumn,
    );

    if (!columnToCopy) {
      toast.error(`Column to copy from '${copyFromColumn}' not found!`);
      return;
    }

    const newColumnData = {
      ...columnToCopy,
      inColChr: newColChar,
    };

    dispatch(saveAsInputColumn(newColumnData));
    toast.success(`Column '${newColChar}' added successfully by copying!`);
    // reset();
    // setMode(null);
  };

  if (!mode && existingInputColumns.length !== 0) {
    return (
      <div className="w-full rounded mx-1 my-4 p-6">
        <h1 className="font-bold text-center text-2xl mb-8">
          Configure Input Columns
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Choose how you want to create your column
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Define New Column Card */}
          <button
            onClick={() => setMode("new")}
            className="group relative bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl p-8 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="bg-white/20 rounded-full p-4 group-hover:bg-white/30 transition-all">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Define New Column</h3>
                <p className="text-blue-100 text-sm">
                  Create a column from scratch with custom properties
                </p>
              </div>
            </div>
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>

          {/* Copy Existing Column Card */}

          <button
            onClick={() => setMode("copy")}
            className="group relative bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl p-8 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="bg-white/20 rounded-full p-4 group-hover:bg-white/30 transition-all">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Copy Existing Column</h3>
                <p className="text-green-100 text-sm">
                  Duplicate properties from an existing column
                </p>
              </div>
            </div>
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Copy mode view
  if (mode === "copy") {
    return (
      <div className="w-full rounded mx-1 my-4">
        <h1 className="font-bold text-center text-2xl m-2">
          Copy from Existing Column
        </h1>
        <div className="w-full p-2 lg:p-4 rounded mx-1">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 ">
            <InputField
              label="New Column Character"
              name="newColumnChar"
              type="text"
              placeholder="Enter new column name"
              value={inputCharCopy}
              onChange={(e) => {
                const value = e.target.value
                  .replace(/[^a-zA-Z]/g, "")
                  .toUpperCase()
                  .trim();
                setinputCharCopy(value);
              }}
              required
              inputRef={inputColumnRef}
            />

            <DropDownMenu
              label="Copy Properties From"
              name="copyFromColumn"
              value={copyFromColumn}
              options={existingInputColumns.map((col) => ({
                value: col.inColChr,
                label: `${col.inColChr}`,
              }))}
              onChange={(e) => setCopyFromColumn(e.target.value)}
              placeholder="Select column to copy"
              required
            />
          </div>

          <div className="flex  mt-4 gap-2 ">
            <div className="">
              <Button
                disabled={!inputCharCopy || !copyFromColumn}
                btnType="save"
                btnText="Create Column"
                handleOnClick={handleCopyCase}
              />
            </div>
            <div className="">
              <Button
                btnType="cancel"
                btnText="Back"
                handleOnClick={() => setMode(null)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded mx-1 my-4">
      <h1 className="font-bold text-center m-2">Configure Input Columns</h1>
      <div
        className="w-full  p-2 lg:p-4 rounded mx-1"
        onKeyDown={handleKeyDown}
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 ">
          <InputField
            label={"Define Column (only character)"}
            name={"inColChr"}
            type="text"
            placeholder={"Enter column name"}
            value={inputColumnData.inColChr}
            toolTip="inputColumnName"
            disabled={isInLocalEditMode}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z]/g, "")
                .toUpperCase()
                .trim();
              e.target.value = value;
              handleOnChange(e);
            }}
            required
            inputRef={inputColumnRef}
          />

          <DropDownMenu
            label="Select Column Data type"
            name="inColDT"
            disabled={isInLocalEditMode}
            toolTip="inputDateType"
            value={inputColumnData.inColDT}
            options={columnDataTypesOptions}
            onChange={(e) => handleOnChange(e)}
            required
          />

          <InputField
            label={"Define Column Contact Name"}
            name={"inColContactName"}
            type="text"
            toolTip="inputColumnContactName"
            placeholder={"Enter column contact name "}
            value={inputColumnData.inColContactName}
            onChange={(e) => {
              handleOnChange(e);
            }}
          />

          <InputField
            label={"Define Column Description "}
            name={"inColDesc"}
            type="text"
            placeholder={"Enter column description"}
            toolTip="inputColumnDesc"
            value={inputColumnData.inColDesc}
            onChange={(e) => {
              handleOnChange(e);
            }}
          />
          {/* 
          <DropDownMenu
            label="Due In Number of Days"
            name="inColDueDate"
            value={inputColumnData.inColDueDate}
            options={dueDateOptions}
            toolTip="inColDueDate"
            onChange={(e) => handleOnChange(e)}
            placeholder="Select due date"
          /> */}
          <DropDownMenu
            label="Due In Number of Days"
            name="inColDueDate"
            value={inputColumnData.inColDueDate}
            options={dueDateOptions}
            toolTip="inColDueDate"
            onChange={(e) => {
              if (e.target.value !== "custom") {
                setCustomDueDate("");
              }
              handleOnChange(e);
            }}
            placeholder="Select due date"
          />

          {inputColumnData.inColDueDate === "custom" && (
            <InputField
              label="Custom Due Days"
              name="inColDueDate"
              type="Number"
              placeholder="Enter number of days"
              value={customDueDate}
              onChange={(e) => {
                let value = Math.abs(Number(e.target.value));
                setCustomDueDate(value);
              }}
            />
          )}

          <DropDownMenu
            disabled={inputColumnData.inColDT !== "3"}
            label="Input Date Format"
            name="inputDateFormat"
            placeholder="Input date format"
            toolTip="inputColumnDateFormat"
            value={inputColumnData.inputDateFormat}
            options={dateFormats}
            onChange={(e) => handleOnChange(e)}
            required={inputColumnData.inColDT === "3"}
          />

          <DropDownMenu
            disabled={inputColumnData.inColDT !== "3"}
            label="Convert To Date Format"
            name="toDateFormat"
            placeholder="Select format"
            toolTip="convertToDateFormat"
            value={inputColumnData.toDateFormat}
            options={dateFormats}
            onChange={(e) => handleOnChange(e)}
          />
          <InputField
            label={"Define Column Character Code"}
            name={"inColCharCode"}
            type="text"
            placeholder={"Enter column character code"}
            value={inputColumnData.inColCharCode}
            toolTip="inputColumnCharCode"
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^a-zA-Z]/g, "")
                .toUpperCase()
                .trim();
              e.target.value = value;
              handleOnChange(e);
            }}
          />
          <InputField
            label={"Account Number"}
            name={"inColAccountNo"}
            type="Number"
            placeholder={"Enter column  account number"}
            toolTip="accountNumber"
            value={inputColumnData.inColAccountNo}
            onChange={(e) => {
              let value = e.target.value;
              value = value < 0 ? value * -1 : value;
              e.target.value = value;
              handleOnChange(e);
            }}
          />
          <InputField
            label={"Enter DC Code"}
            name={"inColDcCode"}
            type="Number"
            placeholder={"Enter column  DC code"}
            value={inputColumnData.inColDcCode}
            toolTip="inputColumnDCCode"
            onChange={(e) => {
              let value = e.target.value;
              value = value < 0 ? value * -1 : value;
              e.target.value = value;
              handleOnChange(e);
            }}
          />
          <InputField
            label={"Enter Exchange Rate"}
            name={"inColExchangeRate"}
            type="Number"
            placeholder={"Enter exchange rate"}
            toolTip="inColExchangeRate"
            value={inputColumnData.inColExchangeRate}
            onChange={(e) => {
              let value = e.target.value;
              value = value < 0 ? value * -1 : value;
              e.target.value = value;
              handleOnChange(e);
            }}
          />
          <div className="col-span-1 xl:col-span-2 flex justify-start items-center gap-x-8">
            <div className="min-h-10 flex">
              <CheckBox
                name={"replaceColumnData"}
                chkBoxText={"Repalce Cell Data"}
                checked={inputColumnData.replaceColumnData}
                onChange={(e) => {
                  if (
                    e.target.checked ||
                    initialInputColumnData.replaceColumnData
                  ) {
                    setIsOpen(true);
                  }
                  handleOnChange(e);
                }}
              />
            </div>
            <div className="min-h-10 flex">
              <CheckBox
                name={"makeColumnAbsolute"}
                chkBoxText={"Make It Absolute"}
                checked={inputColumnData.makeColumnAbsolute}
                disabled={inputColumnData.inColDT !== "2"}
                onChange={(e) => {
                  inputColumnData.inColDT === "2" && handleOnChange(e);
                }}
              />
            </div>
            <div className="min-h-10 flex">
              <CheckBox
                name={"makeItInteger"}
                chkBoxText={"Make It Numeric"}
                checked={inputColumnData.makeItInteger}
                disabled={inputColumnData.inColDT !== "2"}
                onChange={(e) => {
                  inputColumnData.inColDT === "2" && handleOnChange(e);
                }}
              />
            </div>
            <div className="min-h-10 flex">
              <CheckBox
                name={"negateColumnData"}
                chkBoxText={"Convert Values to Negative"}
                checked={inputColumnData.negateColumnData}
                disabled={inputColumnData.inColDT !== "2"}
                onChange={(e) => {
                  inputColumnData.inColDT === "2" && handleOnChange(e);
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="mt-4">
            <Button
              disabled={isSaveDisabled}
              btnType="save"
              btnText={`${isInLocalEditMode ? "Update Column" : "Save Column"}`}
              handleOnClick={handleInsertColumn}
            />
          </div>

          <div className="mt-4">
            <Button
              btnType="cancel"
              btnText={"Clear Column"}
              handleOnClick={reset}
            />
          </div>
        </div>
        <DefineCellData
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setInputColumnData={setInputColumnData}
        />
      </div>
    </div>
  );
};

export default InputColumnForm;
